import React, { useState, useEffect, useCallback } from 'react';
import { Youtube, Mail, RefreshCw, ChevronDown, ChevronUp, UserPlus } from 'react-feather';
import { XIcon } from './XIcon';
import { useNavigate } from 'react-router-dom';
import { integrationsAPI, ConnectionStatus } from '../services/integrationsAPI';
import { useToast } from '../contexts/ToastContext';
import { useConfirm } from '../hooks/useConfirm';
import { useSocialConnect, SocialPlatform } from '../hooks/useSocialConnect';

/**
 * SocialConnectPanel
 *
 * The single source-of-truth UI for "connect your channels" (Gmail / X /
 * YouTube). Used inline on Analytics (when no socials are connected yet)
 * and Messages (always, collapsible). Replaces the old SocialConfigModal
 * dialog — the same component now serves both surfaces, eliminating the
 * two-different-UIs problem.
 *
 * The athleteId resolution is the caller's responsibility. If `athleteId`
 * is null, the panel renders a "finish setup" CTA instead of Connect
 * buttons — this kills the old "Athlete profile not found" toast that
 * fired when guardian-only accounts clicked Connect.
 */

interface Props {
  athleteId: string | null;
  // Fires after a successful connect/disconnect. Parents use this to
  // re-fetch their own data (e.g. analytics endpoints, message lists).
  onRefresh?: () => void;
  // When true, the panel header is clickable and the body collapses.
  // Default collapsed state is derived from connection count.
  collapsible?: boolean;
  // Force initial collapsed state. If omitted, the panel starts expanded
  // when nothing is connected and collapsed when at least one is.
  defaultCollapsed?: boolean;
  hideYouTube?: boolean;
  // Optional title override. Defaults to "Social connections".
  title?: string;
}

const SocialConnectPanel: React.FC<Props> = ({
  athleteId,
  onRefresh,
  collapsible = false,
  defaultCollapsed,
  hideYouTube = false,
  title = 'Social connections',
}) => {
  const navigate = useNavigate();
  const toast = useToast();
  const { ConfirmComponent: DisconnectConfirm, confirm: confirmDisconnect } = useConfirm({
    title: 'Disconnect Account',
    message: 'Are you sure you want to disconnect this account? You will need to reconnect it to use this feature again.',
    confirmText: 'Disconnect',
    cancelText: 'Cancel',
    variant: 'warning',
  });

  const [loading, setLoading] = useState<boolean>(!!athleteId);
  const [connectionStatus, setConnectionStatus] = useState<ConnectionStatus>({
    youtube: false,
    twitter: false,
    gmail: false,
  });
  const [disconnectingPlatform, setDisconnectingPlatform] = useState<string | null>(null);
  const [collapsed, setCollapsed] = useState<boolean>(false);
  // We only derive an initial collapsed state once connection status has
  // arrived; otherwise the user sees the panel briefly expanded then
  // collapse, which is jarring.
  const [collapsedInitialized, setCollapsedInitialized] = useState<boolean>(false);

  const refreshConnectionStatus = useCallback(async () => {
    if (!athleteId) return;
    try {
      const status = await integrationsAPI.getConnectionStatus(athleteId);
      setConnectionStatus(status);
    } catch (err) {
      console.error('Failed to refresh connection status:', err);
    }
  }, [athleteId]);

  useEffect(() => {
    if (!athleteId) {
      setLoading(false);
      return;
    }
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const status = await integrationsAPI.getConnectionStatus(athleteId);
        if (!cancelled) setConnectionStatus(status);
      } catch (err) {
        console.error('Failed to load connection status:', err);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [athleteId]);

  // One-time collapsed initialization once we have real status. After the
  // user clicks the header we leave it alone — they're in control.
  useEffect(() => {
    if (collapsedInitialized || loading) return;
    if (defaultCollapsed !== undefined) {
      setCollapsed(defaultCollapsed);
    } else if (collapsible) {
      const anyConnected = connectionStatus.gmail || connectionStatus.twitter || connectionStatus.youtube;
      setCollapsed(anyConnected);
    }
    setCollapsedInitialized(true);
  }, [collapsedInitialized, loading, defaultCollapsed, collapsible, connectionStatus]);

  const { connect, connecting } = useSocialConnect({
    athleteId,
    onConnected: async () => {
      await refreshConnectionStatus();
      if (onRefresh) onRefresh();
    },
  });
  const busyPlatform = connecting || disconnectingPlatform;

  const handleDisconnect = async (platform: SocialPlatform) => {
    if (!athleteId) return;
    try {
      setDisconnectingPlatform(platform);
      const apiPlatform = platform === 'twitter' ? 'x' : platform;
      if (apiPlatform === 'x') await integrationsAPI.disconnectX(athleteId);
      else if (apiPlatform === 'youtube') await integrationsAPI.disconnectYouTube(athleteId);
      else if (apiPlatform === 'gmail') await integrationsAPI.disconnectGmail(athleteId);
      await refreshConnectionStatus();
      toast.success(`Your ${platform} account has been disconnected.`);
      if (onRefresh) onRefresh();
    } catch (error) {
      console.error(`Error disconnecting ${platform}:`, error);
      toast.error(`Failed to disconnect ${platform}. Please try again.`);
    } finally {
      setDisconnectingPlatform(null);
    }
  };

  const handleClick = async (platform: SocialPlatform) => {
    if (isConnected(platform)) {
      const confirmed = await confirmDisconnect();
      if (confirmed) handleDisconnect(platform);
      return;
    }
    await connect(platform);
  };

  const isConnected = (platform: SocialPlatform): boolean => {
    if (platform === 'youtube') return connectionStatus.youtube;
    if (platform === 'gmail') return connectionStatus.gmail;
    return connectionStatus.twitter;
  };

  const handleManualRefresh = async () => {
    setLoading(true);
    try {
      await refreshConnectionStatus();
      toast.success('Connection status refreshed');
      if (onRefresh) onRefresh();
    } catch (err) {
      console.error(err);
      toast.error('Failed to refresh status');
    } finally {
      setLoading(false);
    }
  };

  const connectedCount =
    (connectionStatus.gmail ? 1 : 0) +
    (connectionStatus.twitter ? 1 : 0) +
    (!hideYouTube && connectionStatus.youtube ? 1 : 0);
  const totalCount = hideYouTube ? 2 : 3;
  const subtitle = `${connectedCount} of ${totalCount} connected`;

  const cardStyle: React.CSSProperties = {
    background: 'white',
    border: '1px solid #E5E7EB',
    borderRadius: 12,
    padding: 20,
    marginBottom: 24,
    fontFamily: 'Manrope, sans-serif',
  };

  // No athlete linked — push them to finish setup. The Connect buttons
  // would otherwise toast "Athlete profile not found" on click, which is
  // confusing.
  if (!athleteId && !loading) {
    return (
      <div style={{ ...cardStyle, textAlign: 'center', padding: '40px 24px' }}>
        <div style={{
          width: 56, height: 56, borderRadius: '50%',
          background: '#FEF3C7', display: 'inline-flex',
          alignItems: 'center', justifyContent: 'center',
          marginBottom: 16,
        }}>
          <UserPlus size={28} color="#D97706" />
        </div>
        <h3 style={{ fontSize: 18, fontWeight: 700, color: '#151133', margin: 0, marginBottom: 8 }}>
          Add your athlete to connect channels
        </h3>
        <p style={{ fontSize: 14, color: '#6B7280', maxWidth: 460, margin: '0 auto 20px', lineHeight: 1.5 }}>
          Social connections are linked to a specific athlete profile. Finish setting one up to enable Gmail, X, and YouTube.
        </p>
        <button
          onClick={() => navigate('/onboarding/quickStart')}
          style={{
            padding: '10px 20px', fontSize: 14, fontWeight: 700, color: 'white',
            background: '#F9550D', border: 'none', borderRadius: 8, cursor: 'pointer',
            boxShadow: '0 4px 12px rgba(249, 85, 13, 0.3)',
          }}
        >
          Continue setup
        </button>
      </div>
    );
  }

  const renderRow = (platform: SocialPlatform, label: string, Icon: typeof Mail, iconColor: string) => (
    <div style={{
      display: 'flex', alignItems: 'center', justifyContent: 'space-between',
      padding: '16px', marginBottom: '12px', background: '#FAFBFF',
      border: '1px solid #E5E7EB', borderRadius: '12px',
    }}>
      <div style={{ display: 'flex', alignItems: 'center', flex: 1 }}>
        <div style={{
          width: 44, height: 44, borderRadius: 12, background: 'white',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          marginRight: 12, border: '1px solid #E5E7EB',
        }}>
          <Icon size={22} color={iconColor} />
        </div>
        <div>
          <div style={{ fontSize: 15, fontWeight: 600, color: '#151133', marginBottom: 2 }}>{label}</div>
          <div style={{ fontSize: 13, color: '#6B7280' }}>
            {isConnected(platform) ? 'Connected' : 'Not connected'}
          </div>
        </div>
      </div>
      <button
        onClick={() => handleClick(platform)}
        disabled={busyPlatform === platform}
        style={{
          background: isConnected(platform) ? '#10B981' : '#F9550D',
          color: 'white', border: 'none', borderRadius: 8,
          padding: '8px 16px', fontSize: 14, fontWeight: 600,
          cursor: busyPlatform === platform ? 'not-allowed' : 'pointer',
          minWidth: 100, opacity: busyPlatform === platform ? 0.6 : 1,
          transition: 'all 0.2s',
        }}
      >
        {busyPlatform === platform
          ? (isConnected(platform) ? 'Working…' : 'Connecting…')
          : (isConnected(platform) ? 'Disconnect' : 'Connect')}
      </button>
    </div>
  );

  const body = (
    <>
      {loading ? (
        <div style={{ textAlign: 'center', padding: '24px 0' }}>
          <div style={{
            width: 32, height: 32, border: '3px solid #E5E7EB',
            borderTop: '3px solid #F9550D', borderRadius: '50%',
            animation: 'spin 1s linear infinite', margin: '0 auto 12px',
          }} />
          <p style={{ fontSize: 13, color: '#6B7280', margin: 0 }}>Loading connections…</p>
        </div>
      ) : (
        <>
          {!hideYouTube && renderRow('youtube', 'YouTube', Youtube, '#FF0000')}
          {renderRow('gmail', 'Gmail', Mail, '#EA4335')}
          {renderRow('twitter', 'X', XIcon, '#000000')}

          <button
            onClick={handleManualRefresh}
            disabled={loading}
            style={{
              width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center',
              padding: '10px 16px', background: 'white',
              border: '1px solid #E5E7EB', borderRadius: 8, fontSize: 13,
              fontWeight: 600, color: '#6B7280',
              cursor: loading ? 'not-allowed' : 'pointer', gap: 8,
            }}
          >
            <RefreshCw size={14} />
            Refresh Status
          </button>
        </>
      )}
    </>
  );

  return (
    <>
      <DisconnectConfirm />
      <style>{`@keyframes spin { from { transform: rotate(0deg);} to { transform: rotate(360deg);} }`}</style>
      <div style={cardStyle}>
        <div
          style={{
            display: 'flex', justifyContent: 'space-between', alignItems: 'center',
            cursor: collapsible ? 'pointer' : 'default',
            marginBottom: collapsed ? 0 : 16,
          }}
          onClick={collapsible ? () => setCollapsed(c => !c) : undefined}
        >
          <div>
            <div style={{ fontSize: 16, fontWeight: 700, color: '#151133' }}>{title}</div>
            <div style={{ fontSize: 13, color: '#6B7280', marginTop: 2 }}>{subtitle}</div>
          </div>
          {collapsible && (
            <div style={{ color: '#6B7280', display: 'flex', alignItems: 'center' }}>
              {collapsed ? <ChevronDown size={20} /> : <ChevronUp size={20} />}
            </div>
          )}
        </div>
        {!collapsed && body}
      </div>
    </>
  );
};

export default SocialConnectPanel;
